import { useEffect, useRef } from "react";
import type { Action } from "@core/features/actions";
import { isOrgAdminAction } from "@core/features/actions";
import {
  useFetchOrganizationInvitations,
  useCreateOrganizationInvitation,
} from "@core/features/organization";
import {
  INVITATION_TYPE,
  useFetchInvitationTemplates,
  type Invitation,
} from "@core/features/invitation";

type Args = {
  action: Action;
};

type Result = {
  invitation: Invitation | null;
  isLoading: boolean;
  error: Error | null;
};

/**
 * Hook to resolve the invitation for an onboarding invitation step.
 *
 * For org admin actions:
 * - Fetches organization invitations
 * - Finds or creates an orgAdmin invitation
 * - Returns the resolved invitation
 *
 * For participant actions:
 * - Extracts invitation directly from action.target_object_data
 */
export function useOnboardingInvitation({ action }: Args): Result {
  const hasAttemptedCreate = useRef(false);
  const isOrgAdmin = isOrgAdminAction(action);

  // For org admin: extract organizationId from target_object_data
  // For participant: extract invitation from target_object_data
  const orgData = action.target_object_data as { id?: number } | null;
  const organizationId = isOrgAdmin ? orgData?.id : undefined;

  // For participant flow, invitation is directly in target_object_data
  const participantInvitation = !isOrgAdmin
    ? (action.target_object_data as Invitation | null)
    : null;

  // Only fetch/create for org admin flow
  const { data: invitations, isLoading: isFetching } =
    useFetchOrganizationInvitations({
      organizationId: organizationId ?? 0,
      enabled: isOrgAdmin && !!organizationId,
    });

  // Fetch orgAdmin template for creating invitations
  const { data: templates, isLoading: isLoadingTemplates } =
    useFetchInvitationTemplates({
      programId: action.program,
      type: INVITATION_TYPE.ORG_ADMIN,
      enabled: isOrgAdmin,
    });

  const {
    mutate: createInvitation,
    error: createError,
    isPending: isCreating,
  } = useCreateOrganizationInvitation({
    organizationId: organizationId ?? 0,
  });

  // Find the orgAdmin or orgAdminOnboarder invitation
  const orgAdminInvitation =
    invitations?.find(
      (inv) =>
        inv.type === INVITATION_TYPE.ORG_ADMIN ||
        inv.type === INVITATION_TYPE.ORG_ADMIN_ONBOARDER
    ) ?? null;

  // Get the first orgAdmin template
  const orgAdminTemplate = templates?.[0] ?? null;

  // Auto-create orgAdmin invitation if none exists and template is available
  useEffect(() => {
    if (
      isOrgAdmin &&
      organizationId &&
      !isFetching &&
      !isLoadingTemplates &&
      invitations &&
      !orgAdminInvitation &&
      orgAdminTemplate &&
      !hasAttemptedCreate.current
    ) {
      hasAttemptedCreate.current = true;
      createInvitation({
        programId: action.program,
        templateId: orgAdminTemplate.id,
        type: INVITATION_TYPE.ORG_ADMIN,
      });
    }
  }, [
    isOrgAdmin,
    organizationId,
    isFetching,
    isLoadingTemplates,
    invitations,
    orgAdminInvitation,
    orgAdminTemplate,
    createInvitation,
    action.program,
  ]);

  // Determine loading state
  // Keep loading until we have an invitation or encounter an error
  // This handles the race condition between mutation completing and query refetching
  const isLoading = isOrgAdmin
    ? isFetching ||
      isLoadingTemplates ||
      isCreating ||
      (!orgAdminInvitation && !createError)
    : false;

  // Determine the invitation to return
  const invitation = isOrgAdmin ? orgAdminInvitation : participantInvitation;

  return {
    invitation,
    isLoading,
    error: createError as Error | null,
  };
}
